/*
  Saffiome landing page (/pages/kids).
  1. <saffiome-lp-form>: first name, last name, email to Klaviyo's client subscriptions API with the public key
     (revision 2026-07-15, the same call the site's email capture has used since 4 Sep 2026), with the profile
     properties the founding offer needs: founding_member_interest, source, page, landing mode and any UTM values.
     202 means accepted. Optional spot-number endpoint (data-api) returns { spot } and personalises the confirmation.
  2. Events, one function, every destination that exists on the page:
       Shopify.analytics.publish (custom web pixels subscribe to these), dataLayer + gtag (GA4),
       fbq (Meta, plus the standard Lead on submit), ttq (TikTok, plus SubmitForm on submit).
     Names: lp_hero_view, lp_scroll_depth {percent}, lp_cta_click {label, target}, lp_form_start {form},
     lp_form_submit {form, source}, lp_offer_view. Buy-box events already on document ("saffiome:event")
     are forwarded as buy_<name>.
  3. The sticky bar on phones: shows once the hero form has scrolled away and no form is on screen.
*/
(function () {
  'use strict';
  // Several sections can include this file on one page (hero form, sign-up boxes); run it once, or every event fires twice.
  if (window.__sfLpLoaded) return;
  window.__sfLpLoaded = true;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function track(name, data) {
    var payload = Object.assign({ page: location.pathname }, data || {});
    try { if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') Shopify.analytics.publish(name, payload); } catch (e) {}
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, payload)); } catch (e) {}
    try { if (typeof window.gtag === 'function') window.gtag('event', name, payload); } catch (e) {}
    try {
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', name, payload);
        if (name === 'lp_form_submit') window.fbq('track', 'Lead', { content_name: 'Kids Day-Night Bundle waitlist' });
      }
    } catch (e) {}
    try {
      if (window.ttq && typeof window.ttq.track === 'function') {
        window.ttq.track(name === 'lp_form_submit' ? 'SubmitForm' : name, payload);
      }
    } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('saffiome:event', { detail: Object.assign({ name: name }, payload) })); } catch (e) {}
  }
  window.SaffiomeLP = window.SaffiomeLP || {};
  window.SaffiomeLP.track = track;

  // Buy-box and cart events already fire "saffiome:event"; forward them, but never our own.
  document.addEventListener('saffiome:event', function (event) {
    var d = event.detail || {};
    if (!d.name || /^lp_/.test(d.name) || /^buy_/.test(d.name)) return;
    var name = 'buy_' + String(d.name).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    var copy = Object.assign({}, d); delete copy.name;
    try { if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') Shopify.analytics.publish(name, copy); } catch (e) {}
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, copy)); } catch (e) {}
  });

  function utm() {
    var out = {};
    try {
      var p = new URLSearchParams(location.search);
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (k) { if (p.get(k)) out[k] = p.get(k).slice(0, 120); });
    } catch (e) {}
    return out;
  }

  function scrollTo(target) {
    var el = target && document.querySelector(target);
    if (!el) return;
    el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    var first = el.querySelector('input:not([type="hidden"]):not([tabindex="-1"])');
    if (first) window.setTimeout(function () { first.focus({ preventScroll: true }); }, reduced ? 0 : 500);
  }

  if (!customElements.get('saffiome-lp-form')) {
    customElements.define('saffiome-lp-form', class extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector('[data-form]');
        this.first = this.querySelector('[data-first]');
        this.email = this.querySelector('[data-email]');
        this.button = this.querySelector('[data-submit]');
        this.status = this.querySelector('[data-status]');
        this.honeypot = this.querySelector('[data-honeypot]');
        this.panel = this.querySelector('[data-done-panel]');
        this.busy = false;
        this.started = false;
        if (!this.form || !this.email || !this.button || !this.status) return;
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        var self = this;
        [this.email, this.first].forEach(function (input) {
          if (!input) return;
          input.addEventListener('focus', function () { self.start(); }, { once: true });
          input.addEventListener('input', function () {
            input.removeAttribute('aria-invalid');
            if (self.status.dataset.state === 'error') self.setStatus('', '');
          });
        });
      }

      start() {
        if (this.started) return;
        this.started = true;
        track('lp_form_start', { form: this.id });
      }

      onSubmit(event) {
        event.preventDefault();
        if (this.busy) return;
        if (this.honeypot && this.honeypot.value !== '') { this.finish({}); return; }
        // Email is the only required field (PR 1, 25 Oct 2026); first name is optional and there is no last name.
        var first = (this.first && this.first.value || '').trim();
        var email = this.email.value.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
          this.email.setAttribute('aria-invalid', 'true');
          this.setStatus(this.dataset.invalidEmail, 'error');
          this.email.focus();
          return;
        }
        this.send({ first: first, email: email });
      }

      async send(who) {
        var list = this.dataset.list;
        var source = this.dataset.source || 'Kids landing page';
        var mode = (document.querySelector('[data-lp-mode]') || {}).dataset ? document.querySelector('[data-lp-mode]').dataset.lpMode : 'waitlist';
        var interest = this.dataset.interest || 'founding';
        var properties = Object.assign({ source: source, page: location.pathname, landing_mode: mode, interest: interest }, utm());
        if (interest === 'adults') { properties.adult_range_interest = true; } else { properties.founding_member_interest = true; properties.founding_priority = true; }
        // A product's own box sends what the parent had chosen in its buy box, as profile properties (PR 1, 25 Oct 2026),
        // so Founders demand and the number of bundles can be seen before orders open. Nothing extra is asked.
        var buySection = this.closest('.sf-buy');
        var buy = buySection ? buySection.querySelector('saffiome-buy') : null;
        if (buy) {
          var planInput = buy.querySelector('input[data-sf-plan]:checked');
          var qtyInput = buy.querySelector('input[data-sf-qty]:checked');
          var planNames = { founding: 'Founders Rate', subscription: 'Standard subscription', one_time: 'One-off' };
          if (planInput) properties.chosen_plan = planNames[planInput.dataset.sfPlanType] || planInput.dataset.sfPlanType;
          properties.chosen_quantity = qtyInput ? Number(qtyInput.value) : 1;
          if (buy.dataset.productHandle) properties.chosen_product = buy.dataset.productHandle;
        }
        var profileAttributes = {
          email: who.email,
          properties: properties,
          subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
        };
        if (who.first) profileAttributes.first_name = who.first;
        var body = {
          data: {
            type: 'subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: profileAttributes
                }
              },
              custom_source: source
            },
            relationships: { list: { data: { type: 'list', id: list } } }
          }
        };
        this.setBusy(true);
        this.setStatus('', '');
        try {
          var response = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(this.dataset.company), {
            method: 'POST',
            headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' },
            body: JSON.stringify(body)
          });
          if (response.status !== 202) { this.setStatus(this.dataset.error, 'error'); return; }
          var spot = null;
          if (this.dataset.api) {
            try {
              var r = await fetch(this.dataset.api, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: who.email, first_name: who.first, source: source }) });
              if (r.ok) { var j = await r.json(); if (j && j.spot) spot = j.spot; }
            } catch (e) {}
          }
          this.finish({ first: who.first, spot: spot });
          track('lp_form_submit', { form: this.id, source: source, has_spot: !!spot });
        } catch (error) {
          this.setStatus(this.dataset.error, 'error');
        } finally {
          this.setBusy(false);
        }
      }

      finish(info) {
        var cap = this.dataset.cap || '100';
        var heading = this.dataset.successHeading || "You're on the list, [first].";
        var bodyHtml = this.dataset.successBody || '';
        if (info.spot) heading = "You're number [spot] on the list, [first].";
        heading = heading.replace('[first]', info.first || 'and thank you').replace('[spot]', info.spot || '').replace('[cap]', cap).replace(', and thank you.', '. Thank you.');
        bodyHtml = bodyHtml.replace(/\[cap\]/g, cap).replace(/\[spot\]/g, info.spot || '');
        this.dataset.done = 'true';
        this.form.reset();
        if (this.panel) {
          var h = this.panel.querySelector('[data-done-heading]'); if (h) h.textContent = heading;
          var b = this.panel.querySelector('[data-done-body]'); if (b) b.innerHTML = bodyHtml;
          this.panel.hidden = false;
          this.panel.focus();
        }
        try { sessionStorage.setItem('sf_lp_joined', '1'); } catch (e) {}
        var sticky = document.querySelector('[data-lp-sticky]');
        if (sticky) sticky.dataset.visible = 'false';
      }

      setBusy(state) {
        this.busy = state;
        this.button.disabled = state;
        this.button.setAttribute('aria-busy', state ? 'true' : 'false');
      }

      setStatus(text, state) {
        this.status.textContent = text || '';
        if (state) this.status.dataset.state = state; else delete this.status.dataset.state;
      }
    });
  }

  function ready(fn) { if (document.readyState !== 'loading') fn(); else document.addEventListener('DOMContentLoaded', fn); }

  ready(function () {
    var hero = document.querySelector('[data-lp-hero]');
    if (!hero) return;

    // CTA clicks and in-page scrolls
    document.addEventListener('click', function (event) {
      var cta = event.target.closest('[data-lp-cta]');
      if (!cta) return;
      track('lp_cta_click', { label: cta.dataset.lpCta, target: cta.dataset.lpScroll || cta.getAttribute('href') || '' });
      if (cta.dataset.lpScroll) { event.preventDefault(); scrollTo(cta.dataset.lpScroll); }
    });

    // Hero and offer views, once each
    if ('IntersectionObserver' in window) {
      var seen = {};
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var name = entry.target.hasAttribute('data-lp-offer') ? 'lp_offer_view' : 'lp_hero_view';
          if (seen[name]) return;
          seen[name] = true;
          track(name, {});
          io.unobserve(entry.target);
        });
      }, { threshold: 0.4 });
      io.observe(hero);
      var offer = document.querySelector('[data-lp-offer]');
      if (offer) io.observe(offer);
    } else {
      track('lp_hero_view', {});
    }

    // Scroll depth at 25, 50, 75, 100
    var marks = [25, 50, 75, 100], fired = {};
    function depth() {
      var doc = document.documentElement;
      var max = doc.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      var pct = Math.round(((window.scrollY || doc.scrollTop) / max) * 100);
      marks.forEach(function (m) { if (pct >= m && !fired[m]) { fired[m] = true; track('lp_scroll_depth', { percent: m }); } });
    }

    // Sticky bar
    var sticky = document.querySelector('[data-lp-sticky]');
    // The hero's own form, never one inside a waitlist pop-up (closed, it measures zero). The simple homepage hero has
    // no form of its own: its button marks where the hero ends.
    var heroForm = Array.prototype.find.call(hero.querySelectorAll('.sf-lpf'), function (f) { return !f.closest('dialog'); }) || hero.querySelector('[data-lp-cta="hero"]');
    function stickyState() {
      if (!sticky) return;
      var joined = false;
      try { joined = sessionStorage.getItem('sf_lp_joined') === '1'; } catch (e) {}
      if (joined) { sticky.dataset.visible = 'false'; return; }
      var anyFormVisible = Array.prototype.some.call(document.querySelectorAll('.sf-lpf'), function (f) {
        var r = f.getBoundingClientRect();
        return r.bottom > 0 && r.top < window.innerHeight;
      });
      var pastHero = heroForm ? heroForm.getBoundingClientRect().bottom < 0 : window.scrollY > 600;
      var show = pastHero && !anyFormVisible;
      sticky.dataset.visible = show ? 'true' : 'false';
      sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      var btn = sticky.querySelector('button, a');
      if (btn) btn.tabIndex = show ? 0 : -1;
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { depth(); stickyState(); ticking = false; });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();
  });
})();
